import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const VISIBLE = 3;
const GAP = 30;
const EDGE_FADE = 350;
const SPEED = 40;
const SECONDS_PER_CARD = 7;
const RESTART_DELAY = 3000;
const MAX_DT = 1 / 30;

// UI styles
const CarouselViewport = styled.div`
  position: relative;
  width: 100vw;
  max-width: 850px;
  padding-top: 50px;
  overflow: visible;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 50px;
    height: 100%;
    width: ${EDGE_FADE}px;
    pointer-events: none;
    z-index: 4;
  }

  &::before {
    left: -${EDGE_FADE}px;
    background: linear-gradient(
      to left,
      rgba(237,241,245, 0.5) 0%,
      rgba(237,241,245, 1) 100%
    );
  }

  &::after {
    right: -${EDGE_FADE}px;
    background: linear-gradient(
      to right,
      rgba(237,241,245, 0.5) 30%,
      rgba(237,241,245, 1) 100%
    );
  }
`;

const Track = styled.div`
  display: flex;
  height: 100%;
  will-change: transform;
  backface-visibility: hidden;
  transition: none;
  transform: translate3d(${({ offsetPx }) => `-${offsetPx}px`}, 0, 0);
`;

const Card = styled.div`
  flex: 0 0 auto;
  margin: 0;
  width: ${({ cardW }) => `${cardW}px`};
  margin-right: ${GAP}px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  padding: 18px;
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 10px;
`;

const Blurb = styled.p`
  font-size: 14px;
  margin: 0 0 12px;
  color: #333;
  line-height: 1.4;
`;

const StrengthsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const EachTitle = styled.div`
  display: flex;
  flex-direction: row;
  padding: 5px 10px;
  background: #e5e5e5;
  border-radius: 100px;
  font-size: 13px;
`;

const Metrics = styled.div`
  font-size: 14px;
  color: #444;
  margin-top: auto;
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 60%;
  transform: translateY(-50%);
  ${({ left }) => (left ? "left: -55px;" : "right: -55px;")}
  @media (max-width: 900px) {
    ${({ left }) => (left ? "left: 45px;" : "right: 45px;")}
  }

  background: #fff;
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.30);
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  color: #333;
  transition: background 0.2s;

  &:hover { background: #f0f0f0; }
`;

// Carousel component
export default function CandidatesCarousel({ candidates = [] }) {
  const len = candidates.length;
  const tripled = useMemo(() => {
    const withIdx = candidates.map((c, i) => ({ ...c, _orig: i }));
    return [...withIdx, ...withIdx, ...withIdx];
  }, [candidates]);

  const START = len;
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    let last = 0;
    const commit = (w) => {
      if (w === last) return;
      last = w;
      setContainerW(w);
    };
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const w = entry?.contentBoxSize
        ? (Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0].inlineSize
            : entry.contentBoxSize.inlineSize)
        : entry?.contentRect?.width ?? el.clientWidth;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => commit(Math.round(w)));
    });
    ro.observe(el);
    last = Math.round(el.getBoundingClientRect().width);
    setContainerW(last);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const cardW = Math.max(0, (containerW - GAP * (VISIBLE - 1)) / VISIBLE);
  const stepPx = cardW + GAP;

  const [offsetPx, setOffsetPx] = useState(START * stepPx);
  const posRef = useRef(START * stepPx);
  const pausedRef = useRef(false);
  const speedRef = useRef(SPEED);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const restartRef = useRef(null);

  useEffect(() => {
    if (stepPx > 0) {
      speedRef.current = stepPx / SECONDS_PER_CARD;
    } else {
      speedRef.current = SPEED;
    }
  }, [stepPx]);

  useEffect(() => {
    const newPos = START * stepPx || 0;
    posRef.current = newPos;
    setOffsetPx(newPos);
    lastTsRef.current = 0;
  }, [stepPx, START]);

  useEffect(() => {
    const loop = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const raw = (ts - lastTsRef.current) / 1000;
      const dt = Math.min(raw, MAX_DT);
      lastTsRef.current = ts;

      if (!pausedRef.current && stepPx > 0 && len > 0) {
        posRef.current += speedRef.current * dt;
        const startPx = START * stepPx;
        const endPx = (START + len) * stepPx;
        while (posRef.current >= endPx) posRef.current -= len * stepPx;
        while (posRef.current < startPx) posRef.current += len * stepPx;
        setOffsetPx(posRef.current);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [len, START, stepPx]);

  useEffect(() => {
    const handleVis = () => { lastTsRef.current = 0; };
    document.addEventListener("visibilitychange", handleVis);
    return () => document.removeEventListener("visibilitychange", handleVis);
  }, []);

  const pauseAndResume = () => {
    pausedRef.current = true;
    if (restartRef.current) clearTimeout(restartRef.current);
    restartRef.current = setTimeout(() => {
      lastTsRef.current = 0;
      pausedRef.current = false;
    }, RESTART_DELAY);
  };

  const normalize = () => {
    const startPx = START * stepPx;
    const endPx = (START + len) * stepPx;
    while (posRef.current >= endPx) posRef.current -= len * stepPx;
    while (posRef.current < startPx) posRef.current += len * stepPx;
    setOffsetPx(posRef.current);
  };

  const goPrev = () => {
    posRef.current -= stepPx;
    normalize();
    pauseAndResume();
  };

  const goNext = () => {
    posRef.current += stepPx;
    normalize();
    pauseAndResume();
  };

  return (
    <CarouselViewport ref={containerRef}>
      <ArrowButton left onClick={goPrev}><FaChevronLeft /></ArrowButton>
      <ArrowButton onClick={goNext}><FaChevronRight /></ArrowButton>

      <Track offsetPx={offsetPx}>
        {tripled.map((c, i) => (
          <Card key={`${c._orig}-${i}`} cardW={cardW}>
            <Name>{c.name}</Name>
            <Title>{c.title}</Title>
            <Blurb>{c.blurb}</Blurb>
            <StrengthsWrap>
              {c.strengths?.map((s, idx) => (
                <EachTitle key={idx}>{s}</EachTitle>
              ))}
            </StrengthsWrap>
            <Metrics>{c.metrics?.join(" • ")}</Metrics>
          </Card>
        ))}
      </Track>
    </CarouselViewport>
  );
}
